import { Logger, Trace } from '@gitbook/util-debug';
import { normalizeURLSlug, validateURLSlug } from '@gitbook/util-url';

import { normalizeReusableContentTitle } from '../../reusableContent';
import {
    type Revision,
    type RevisionConflicts,
    type RevisionPage,
    type RevisionPageComputed,
    type RevisionPageDocument,
    type RevisionPageLink,
    type RevisionPageParent,
    RevisionPageType,
    type RevisionReusableContents,
    type RevisionVariables,
} from '../../types';
import { isYValue, mutations, yToJSON } from '../../utils';
import { validateVariableName } from '../variables';

const logger = Logger('normalizeRevision');

/**
 * The schema version of the normalization process.
 * Change it when the normalization process changes in a breakin way that should require all revisions to be re-normalized.
 */
const SCHEMA_VERSION = 1;

/**
 * Cleans up a Revision from invalid data, such as:
 * - pages, sub-pages of pages and root pages without parent
 * - non-existing conflicting pages
 *
 * It returns true if a change was made to the revision.
 */
export function normalizeRevision(
    revision: mutations.MutableData<Revision>,
    options: {
        /**
         * If true, if the schema version of the revision is equals to the current one, the normalization will be skipped.
         * @default false
         */
        checkSchemaVersion?: boolean;

        /**
         * If true, the schema version of the revision will be updated to the current one.
         * It should only be set to true when we know that the revision will not be mutated again.
         * For safety, during development, the revision will be frozen after the schema version is updated.
         * @default false
         */
        updateSchemaVersion?: boolean;
    } = {}
): boolean {
    const { checkSchemaVersion = false, updateSchemaVersion = false } = options;

    if (checkSchemaVersion) {
        const schemaVersion = mutations.get(revision, 'schemaVersion');
        if (schemaVersion === SCHEMA_VERSION) {
            return false;
        }
    }

    const id = mutations.get(revision, 'id');
    const contentId = mutations.get(revision, 'contentId');

    const trace = new Trace('hive.normalizeRevision', { sampleRate: 0, silent: true });
    trace.setLabels({
        contentId,
        revisionId: id,
    });

    return trace.wrapSync(() => {
        let changed = false;

        const pages = mutations.get(revision, 'pages');
        const files = mutations.get(revision, 'files');
        const conflicts = mutations.get(revision, 'conflicts');
        const parents = mutations.get(revision, 'parents');
        const pageIds = new Set(mutations.keys(pages));

        if (!parents) {
            throw new Error(`Revision ${contentId}/${id} has no parents`);
        }

        // Validate and normalize parents
        if (parents.length > 3) {
            throw new Error(`Revision ${contentId}/${id} has more than 3 parents`);
        }
        const parentsArray = mutations.values(parents);
        if (parentsArray.includes(id)) {
            const newParents = parentsArray.filter((parentId) => parentId !== id);
            if (newParents.length === 0) {
                throw new Error(
                    `Revision ${contentId}/${id} has no more parents after removing itself as parent`
                );
            }
            changed = true;
            mutations.updateInArray(parents, newParents);

            logger.warning(
                `${contentId}/${id}: updated parents to remove itself as parent. New parents:`,
                newParents
            );
        }

        // Validate and normalize variables
        const variables = mutations.get(revision, 'variables');
        if (variables) {
            if (normalizeVariables(variables)) {
                changed = true;
            }
        }

        // Validate and normalize reusable content
        const reusableContents = mutations.get(revision, 'reusableContents');
        if (!reusableContents) {
            changed = true;
            mutations.set(revision, 'reusableContents', {});
        } else {
            if (normalizeReusableContents(reusableContents)) {
                changed = true;
            }
        }

        const normalizePages = (
            pages: string[] | mutations.MutableData<string[]>
        ): string[] | undefined => {
            const validPages: string[] = [];
            pages.forEach((pageId: string) => {
                if (normalizePage(pageId)) {
                    validPages.push(pageId);
                }
            });

            if (validPages.length === pages.length) {
                return undefined;
            }

            return validPages;
        };

        const normalizeFile = (fileId: string) => {
            const file = mutations.get(files, fileId);
            if (!file) {
                return;
            }

            const downloadURL = mutations.get(file, 'downloadURL');
            if (!downloadURL) {
                changed = true;
                // Some old files might not have a downloadURL, we should remove them
                mutations.del(files, fileId);

                logger.warning(`${contentId}/${id}: no downloadURL for file ${fileId}`);
                return;
            }
        };

        const normalizePage = (pageId: string): boolean => {
            if (!pageIds.has(pageId)) {
                return false;
            }

            pageIds.delete(pageId);

            const page = mutations.get(pages, pageId);
            if (!page) {
                return false;
            }

            // Validate and normalize title
            const title = mutations.get(page, 'title');
            if (typeof title !== 'string' || title.length === 0) {
                changed = true;
                mutations.set(page, 'title', 'Untitled');
                return true;
            }

            if (mutations.get(page, 'type') === RevisionPageType.Link) {
                if (
                    normalizePageLink(
                        contentId,
                        id,
                        pageId,
                        page as mutations.MutableData<RevisionPageLink>
                    )
                ) {
                    changed = true;
                }
                return true;
            }

            if (mutations.get(page, 'type') === RevisionPageType.Computed) {
                if (
                    normalizePageComputed(
                        contentId,
                        id,
                        pageId,
                        page as mutations.MutableData<RevisionPageComputed>
                    )
                ) {
                    changed = true;
                }
                return true;
            }

            if (checkIsRevisionPageDocument(page)) {
                // Some slugs might be invalid due to the issue fixed by https://github.com/GitbookIO/gitbook-x/pull/10821
                // Normalize the page slug if needed so future revisions are fixed
                if (mutations.get(page, 'slug')) {
                    if (normalizePageSlug(contentId, id, pageId, page)) {
                        changed = true;
                    }
                }

                // Validate and normalize variables
                const variables = mutations.get(page, 'variables');
                if (variables) {
                    if (normalizeVariables(variables)) {
                        changed = true;
                    }
                }
            }

            if (checkIsRevisionPageParent(page)) {
                let childrenPages = mutations.get(page, 'pages');

                // Ensure that every parent page has a pages array, even if empty.
                if (childrenPages === undefined) {
                    changed = true;
                    mutations.set(page, 'pages', []);
                    childrenPages = mutations.get(page, 'pages');
                }

                const normalizedChildrenPages = normalizePages(childrenPages);
                if (normalizedChildrenPages) {
                    changed = true;
                    mutations.updateInArray(childrenPages, normalizedChildrenPages);

                    logger.warning(
                        `${contentId}/${id}: updated children for page ${pageId}. New children pages:`,
                        normalizedChildrenPages
                    );
                }
            }

            return true;
        };

        // rootPages should aways be defined.
        let rootPages = mutations.get(revision, 'rootPages');
        if (rootPages === undefined) {
            changed = true;
            mutations.set(revision, 'rootPages', []);
            rootPages = mutations.get(revision, 'rootPages');
        }

        // Validate and normalize rootPages, this will also normalize all of their children pages.
        const normalizedRootPages =
            // Put all pageIds in rootPages if it's empty, the cleanup will be done later in the logic
            // once iterated on all pages we will remove non root pages from rootPages.
            normalizePages(rootPages.length === 0 ? Array.from(pageIds) : rootPages);
        if (normalizedRootPages) {
            changed = true;
            mutations.updateInArray(rootPages, normalizedRootPages);

            logger.warning(
                `${contentId}/${id}: normalized rootPages. New root pages:`,
                normalizedRootPages
            );
        }

        // Make pages that don't have a valid parent into rootPages
        if (pageIds.size > 0) {
            const newRootPageIds = Array.from(pageIds);
            mutations.push(rootPages, newRootPageIds);
            changed = true;

            logger.warning(
                `${contentId}/${id}: made pages with invalid parent into rootPages. Pages:`,
                newRootPageIds
            );
        }

        // Validate all the files
        for (const fileId of mutations.keys(files)) {
            normalizeFile(fileId);
        }

        // Validate that in conflicts we are not referencing no-longer existing pages and page links.
        // We also remove the conflicts in case all have been resolved.
        if (conflicts !== undefined) {
            const conflictPages = mutations.get(conflicts as RevisionConflicts, 'pages');
            const conflictPageIds = mutations.keys(conflictPages);

            for (const pageId of conflictPageIds) {
                const conflictPage = mutations.get(pages, pageId);
                // If the page is not found in the revision, or is not a document,
                // we remove it from the conflicts
                if (
                    !conflictPage ||
                    mutations.get(conflictPage, 'type') !== RevisionPageType.Document
                ) {
                    changed = true;
                    mutations.del(conflictPages, pageId);
                }
            }

            // If after removing all invalid conflicts, there are no conflicts left, we remove the conflicts property
            if (mutations.keys(conflictPages).length === 0) {
                changed = true;
                mutations.del(revision, 'conflicts');

                logger.warning(
                    `${contentId}/${id}: removed conflicts. All were invalid and have been removed.`
                );
            } else {
                // And if we're left with only resolved conflicts, we remove them as well
                if (
                    mutations
                        .values(conflictPages)
                        .every((conflictState) => conflictState === false)
                ) {
                    changed = true;
                    mutations.del(revision, 'conflicts');

                    logger.warning(
                        `${contentId}/${id}: removed conflicts. All were resolved and have been removed.`
                    );
                }
            }
        }

        if (updateSchemaVersion && mutations.get(revision, 'schemaVersion') !== SCHEMA_VERSION) {
            if (isYValue(revision)) {
                throw new Error(
                    `Cannot update schema version of revision ${contentId}/${id} because it is a YValue`
                );
            }

            changed = true;
            mutations.set(revision, 'schemaVersion', SCHEMA_VERSION);

            if (process.env.NODE_ENV !== 'production') {
                deepFreeze(revision);
            }
        }

        return changed;
    });
}

function checkIsRevisionPageDocument(
    page: mutations.MutableData<RevisionPage>
): page is mutations.MutableData<RevisionPageDocument> {
    return mutations.get(page, 'type') === RevisionPageType.Document;
}

function checkIsRevisionPageParent(
    page: mutations.MutableData<RevisionPage>
): page is mutations.MutableData<RevisionPageParent> {
    return (
        mutations.get(page, 'type') === RevisionPageType.Document ||
        mutations.get(page, 'type') === RevisionPageType.Group
    );
}

/**
 * Normalize page or revision variables.
 */
export function normalizeVariables(variables: mutations.MutableData<RevisionVariables>): boolean {
    let changed = false;
    for (const [name, value] of mutations.entries(variables)) {
        if (
            typeof name !== 'string' ||
            !validateVariableName(name) ||
            (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean')
        ) {
            changed = true;
            mutations.del(variables, name);
        }
    }

    return changed;
}

/**
 * Normalize reusable contents.
 */
function normalizeReusableContents(
    reusableContents: mutations.MutableData<RevisionReusableContents>
): boolean {
    let changed = false;
    for (const [_, reusableContent] of mutations.entries(reusableContents)) {
        // Validate and normalize title
        const title = mutations.get(reusableContent, 'title');
        if (typeof title !== 'string' || title.length === 0) {
            changed = true;
            mutations.set(reusableContent, 'title', 'Untitled');
            continue;
        }

        const normalizedTitle = normalizeReusableContentTitle(title);
        if (normalizedTitle !== title) {
            changed = true;
            mutations.set(reusableContent, 'title', normalizedTitle);
        }
    }

    return changed;
}

/**
 * Normalize a page link.
 */
function normalizePageLink(
    contentId: string,
    revisionId: string,
    pageId: string,
    pageLink: mutations.MutableData<RevisionPageLink>
): boolean {
    const target = mutations.get(pageLink, 'target');
    const targetKind = typeof target !== 'object' ? undefined : mutations.get(target, 'kind');
    const targetSpace = typeof target !== 'object' ? undefined : mutations.get(target, 'space');

    if (typeof targetKind !== 'string') {
        logger.warning(
            `${contentId}/${revisionId}: normalized invalid link ${pageId}, with target ${JSON.stringify(
                yToJSON(target)
            )}`
        );

        // Invalid target, convert page to a document
        // @ts-ignore
        mutations.set(pageLink, 'type', RevisionPageType.Document);
        // @ts-ignore
        mutations.set(pageLink, 'pages', []);
        mutations.del(pageLink, 'target');

        return true;
    }
    if (targetKind === 'collection' && typeof targetSpace !== 'undefined') {
        // Autofix for https://linear.app/gitbook-x/issue/RND-4519/space-w8zhwnvbxt3w0rdsfdqk-crash-firebaseerror-function-doc-cannot-be
        // Fix for the original issue leading to invalid revisions in https://github.com/GitbookIO/gitbook-x/pull/13254
        // Because this issue was caused by a bug in the codebase and might have affected multiple revisions, we can't fix it everywhere
        // so we use this autofix to prevent the crash and allowing the users to have a valid revision again
        mutations.del(target, 'space');
        mutations.set(target, 'collection', targetSpace);
        mutations.set(pageLink, 'target', target);

        return true;
    }

    return false;
}

/**
 * Normalize a page computed.
 */
function normalizePageComputed(
    _contentId: string,
    _revisionId: string,
    _pageId: string,
    pageComputed: mutations.MutableData<RevisionPageComputed>
): boolean {
    const type = mutations.getIn(pageComputed, ['computed', 'type'] as const);

    // @ts-ignore - this is what we want to normalize
    if (type === 'integration:openapi:generate' || type === 'core:openapi') {
        mutations.setIn(pageComputed, ['computed', 'type'] as const, 'builtin:openapi');
        return true;
    }

    // @ts-expect-error - this is what we want to normalize
    const integration = mutations.getIn(pageComputed, ['computed', 'integration'] as const);
    // @ts-expect-error - this is what we want to normalize
    const source = mutations.getIn(pageComputed, ['computed', 'source'] as const);

    if (typeof integration === 'string' && typeof source === 'string') {
        if (integration === 'openapi' && source === 'generate') {
            mutations.setIn(pageComputed, ['computed', 'type'] as const, 'builtin:openapi');
        } else {
            mutations.setIn(
                pageComputed,
                ['computed', 'type'] as const,
                `integration:${integration}:${source}`
            );
        }
        // @ts-expect-error - this is what we want to normalize
        mutations.delIn(pageComputed, ['computed', 'source'] as const);
        // @ts-expect-error - this is what we want to normalize
        mutations.delIn(pageComputed, ['computed', 'integration'] as const);
        return true;
    }

    // Nothing to do for now
    return false;
}

/**
 * Normalize a page slug.
 */
function normalizePageSlug(
    contentId: string,
    revisionId: string,
    pageId: string,
    pageDocument: mutations.MutableData<RevisionPageDocument>
): boolean {
    const slug = mutations.get(pageDocument, 'slug');
    if (!slug || validateURLSlug(slug)) {
        return false;
    }

    const normalized = normalizeURLSlug(slug);
    mutations.set(pageDocument, 'slug', normalized);
    logger.warning(
        `Normalized invalid slug ${slug} as ${normalized}. contentId=${contentId} revisionId=${revisionId} pageId=${pageId}`
    );

    return true;
}

function deepFreeze(o: mutations.MutableData<Revision>) {
    for (const key in o) {
        const c = o[key];
        if (typeof c === 'object' || typeof c === 'function') {
            deepFreeze(o[key]);
        }
    }
    return Object.freeze(o);
}
